import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import FFLogo from "../../../img/FREEFIT_LOGO_new.png";
import { formatDate } from "../../../utils/dateUtils";
import { isV2Enabled, V2_SCREEN_KEYS } from "../../registry";
import ScreenHeader from "../../components/ScreenHeader";
import { PASS_STATE } from "../../hooks/usePassState";
import useProfileData from "./useProfileData";
import "./profile.scss";

// V2 profile page ("פרופיל"). Routed at /profile, reached from the hamburger
// (V2 partners only — see HeaderIconBurger). No V1 counterpart, so the data
// logic lives in useProfileData. Will later be linked from the new home page.

const ChevronLeft = () => (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CouponIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 8V6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V8a2.5 2.5 0 0 0 0 5v1.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 14.5V13a2.5 2.5 0 0 0 0-5Z"
      transform="translate(0 1.5)"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14.5 8.5v6" transform="translate(0 1.5)" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="1.5 2.5" />
  </svg>
);

const OrdersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="5" width="17" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 2.5V6M16 2.5V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="8" cy="14" r="1" fill="currentColor" />
    <circle cx="12" cy="14" r="1" fill="currentColor" />
    <circle cx="16" cy="14" r="1" fill="currentColor" />
  </svg>
);

const PaymentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.5 9.5h19" stroke="currentColor" strokeWidth="1.8" />
    <path d="M6 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1.1" fill="currentColor" />
  </svg>
);

const FaqIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9.6 9.2a2.4 2.4 0 1 1 3.3 2.9c-.7.35-.9.9-.9 1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="3" width="14" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// Path data from img/svg/whatsapp_icon_without_backround.svg, inlined with
// currentColor (the asset itself is white-filled — invisible on white rows).
const WhatsappIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.1941 21.3402C10.2215 21.3402 8.24897 20.8022 6.63504 19.7263L2.86921 20.9816L4.12448 17.3951C3.04853 15.7811 2.33123 13.8085 2.33123 11.6566V10.76C2.86921 5.7389 6.99369 1.79374 12.1941 1.79374C17.3946 1.79374 21.5191 5.7389 22.057 10.9393V11.6566C22.057 17.0364 17.5739 21.3402 12.1941 21.3402ZM23.8503 11.298C23.671 5.0216 18.4705 0.000488281 12.1941 0.000488281C5.91774 0.000488281 0.717302 4.84227 0.358651 11.1187V11.6566C0.358651 13.8085 1.07595 15.9605 2.15191 17.5744L0 23.8508L6.45572 21.8782C8.06964 22.7748 10.0422 23.3128 12.1941 23.3128C18.6498 23.3128 23.8503 18.1124 23.8503 11.6566V11.298Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.5737 13.9874C17.2151 13.808 15.7805 13.0907 15.6012 13.0907C15.2425 12.9114 15.0632 12.9114 14.8839 13.2701C14.7045 13.4494 14.1666 14.1667 13.9872 14.346C13.8079 14.5253 13.6286 14.5253 13.4493 14.346C13.0906 14.1667 12.194 13.9874 11.118 12.9114C10.2214 12.1941 9.68342 11.2975 9.5041 10.9388C9.32477 10.7595 9.5041 10.5802 9.5041 10.4009L10.0421 9.86288V9.68355C10.2214 9.68355 10.2214 9.50422 10.2214 9.50422C10.4007 9.14557 10.4007 9.14557 10.2214 8.96625C10.2214 8.78692 9.68342 7.35232 9.32477 6.81434C9.14545 6.27637 8.96612 6.27637 8.7868 6.27637H8.24882C8.0695 6.27637 7.71084 6.45569 7.35219 6.63502C7.17287 6.99367 6.45557 7.71097 6.45557 8.96625C6.45557 9.3249 6.45557 9.68355 6.63489 10.0422C6.81422 10.9388 7.53152 11.8355 7.53152 12.0148C7.71084 12.1941 9.5041 15.2426 12.5526 16.3186C15.4218 17.3945 15.4218 17.0359 15.9598 17.0359C16.4978 17.0359 17.7531 16.3186 17.9324 15.6013C18.1117 15.0633 18.1117 14.346 18.1117 14.346C17.9324 14.1667 17.7531 14.1667 17.5737 13.9874Z"
      fill="currentColor"
    />
  </svg>
);

function PassBanner({ t, passArt, onClick }) {
  return (
    <button type="button" className="pf-v2-banner" onClick={onClick}>
      <span className="pf-v2-banner-texts">
        <span className="pf-v2-banner-title">{t("PROFILE_PASS_BANNER_TITLE")}</span>
        <span className="pf-v2-banner-body">
          {t("PROFILE_PASS_BANNER_BODY")} <span className="pf-v2-banner-link">{t("PASS_BANNER_LINK")}</span>
        </span>
      </span>
      {passArt && <img src={passArt} alt="" className="pf-v2-banner-art" />}
    </button>
  );
}

function PassCard({ t, isActive, endDate, onClick }) {
  return (
    <button type="button" className="pf-v2-pass-card" onClick={onClick}>
      <span className={`pf-v2-pass-card-icon${isActive ? "" : " pf-v2-pass-card-icon--inactive"}`}>
        <CouponIcon />
      </span>
      <span className="pf-v2-pass-card-texts">
        <span className="pf-v2-pass-card-title">{t("MY_MULTIPLE_ENTRY_PASS")}</span>
        <span className="pf-v2-pass-card-subtitle">
          {isActive ? t("PASS_ACTIVE_LABEL") : t("EXPIRED", { date: formatDate(endDate) })}
        </span>
      </span>
      <span className="pf-v2-pass-card-chevron">
        <ChevronLeft />
      </span>
    </button>
  );
}

function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partner, firstName, lastName, cardNumber, passState, showPassPromo, showPayment, paymentPath, passArt } =
    useProfileData();

  // Direct URL hit by a partner outside the rollout → home.
  if (!isV2Enabled(V2_SCREEN_KEYS.PROFILE, partner)) return <Navigate to="/" replace />;

  const navItems = [
    { id: "orders", labelKey: "MY_ORDERS", Icon: OrdersIcon, onClick: () => navigate("/orderHistory") },
    ...(showPayment
      ? [{ id: "payment", labelKey: "PAYMENT_METHOD", Icon: PaymentIcon, onClick: () => navigate(paymentPath) }]
      : []),
    { id: "howItWorks", labelKey: "HOW_IT_WORKS", Icon: InfoIcon, onClick: () => navigate("/how-it-works") },
    { id: "faq", labelKey: "FAQ", Icon: FaqIcon, onClick: () => navigate("/faq") },
    { id: "terms", labelKey: "TERMS_OF_SERVICE", Icon: DocumentIcon, onClick: () => navigate("/terms-external?type=terms") },
    { id: "privacy", labelKey: "PRIVACY_POLICY", Icon: DocumentIcon, onClick: () => navigate("/terms-external?type=privacy") },
    { id: "contact", labelKey: "CONTACT_US", Icon: WhatsappIcon, onClick: () => window.open(`https://wa.me/972559458809`, "_blank") },
  ];

  return (
    <div className="pf-v2">
      {/* Profile is a top-level destination — back always exits to home rather than
          popping history, which could be a mid-funnel screen or nothing at all. */}
      <ScreenHeader title={t("PROFILE_TITLE")} onBack={() => navigate("/")} />

      <div className="pf-v2-body">
        <div className="pf-v2-identity">
          <p className="pf-v2-identity-name">
            {firstName} {lastName}
          </p>
          <span className="pf-v2-identity-chip">
            {t("SUBSCRIPTION_NUMBER")}: {cardNumber}
          </span>
        </div>

        {showPassPromo && (
          <PassBanner t={t} passArt={passArt} onClick={() => navigate("/discover-pass-card")} />
        )}
        {(passState?.state === PASS_STATE.ACTIVE || passState?.state === PASS_STATE.INACTIVE) && (
          <PassCard
            t={t}
            isActive={passState.state === PASS_STATE.ACTIVE}
            endDate={passState.endDate}
            onClick={() => navigate("/multipleEntryPass")}
          />
        )}

        <p className="pf-v2-section-title">{t("SETTINGS")}</p>
        <nav className="pf-v2-nav">
          {navItems.map(({ id, labelKey, Icon, onClick }) => (
            <button type="button" key={id} className="pf-v2-nav-row" onClick={onClick}>
              <span className="pf-v2-nav-row-icon">
                <Icon />
              </span>
              <span className="pf-v2-nav-row-label">{t(labelKey)}</span>
              <span className="pf-v2-nav-row-chevron">
                <ChevronLeft />
              </span>
            </button>
          ))}
        </nav>
      </div>

      <footer className="pf-v2-footer">
        {/* WhatsApp service note — hidden while the contact menu item above is the
            entry point; kept (with its i18n keys and scss) in case it's needed again.
        <p className="pf-v2-footer-note">
          <span className="pf-v2-footer-note-line">
            {t("PROFILE_WHATSAPP_SERVICE_NOTE")}
            <WhatsappIcon />
          </span>
          <br />
          055-9458809
        </p>
        */}
        <img src={FFLogo} alt="freefit" className="pf-v2-logo" />
      </footer>
    </div>
  );
}

export default Profile;
